'use client';

/**
 * GlassChip Component
 *
 * A chip with subtle glow effects and smooth animations.
 * Automatically adapts to light/dark theme.
 */

import Chip, { type ChipProps } from '@mui/material/Chip';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { forwardRef } from 'react';
import { glowEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface GlassChipProps extends ChipProps {
  /**
   * Enable hover glow effect
   * @default true
   */
  glowOnHover?: boolean;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Enable scale animation on hover
   * @default false
   */
  hoverScale?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledChipProps {
  $isDark: boolean;
  $glowOnHover: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $hoverScale: boolean;
  $chipColor: ChipProps['color'];
  $chipVariant: ChipProps['variant'];
}

const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

const StyledChip = styled(
  Chip,
  transientOptions,
)<StyledChipProps>(({ theme, $isDark, $glowOnHover, $glowColor, $hoverScale, $chipColor, $chipVariant }) => {
  const isOutlined = $chipVariant === 'outlined';
  const colorKey = $chipColor === 'default' ? 'primary' : ($chipColor ?? 'primary');
  const mainColor = theme.palette[colorKey]?.main ?? theme.palette.primary.main;
  const vibrant = $isDark ? (theme.palette[colorKey]?.dark ?? mainColor) : mainColor;

  const isMaterial = theme.designGuideline === 'material';

  // ── Material Design 3 ──
  if (isMaterial) {
    return {
      position: 'relative',
      overflow: 'hidden',
      transition: transitionPresets.button,
      fontWeight: 500,
      borderRadius: 8, // MD3 chip radius
      fontSize: '0.8125rem',
      height: 32, // MD3 chip height

      '&.MuiChip-sizeSmall': {
        height: 24,
        fontSize: '0.75rem',
      },

      // ── MD3 Filled — solid tonal surface ──
      ...(!isOutlined && {
        background: $isDark ? alpha(mainColor, 0.2) : alpha(mainColor, 0.12),
        color: $isDark ? (theme.palette[colorKey]?.light ?? mainColor) : (theme.palette[colorKey]?.dark ?? mainColor),
        border: 'none',
        boxShadow: 'none',
      }),

      // ── MD3 Outlined — transparent with outline ──
      ...(isOutlined && {
        background: 'transparent',
        borderColor: $isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        boxShadow: 'none',
        color: $isDark ? (theme.palette[colorKey]?.light ?? mainColor) : mainColor,
      }),

      '&:hover': {
        ...($hoverScale && {
          transform: 'scale(1.02)',
        }),
        ...(!isOutlined && {
          background: $isDark ? alpha(mainColor, 0.28) : alpha(mainColor, 0.18),
        }),
        ...(isOutlined && {
          background: alpha(mainColor, 0.08),
          borderColor: $isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)',
        }),
      },

      '&:active': {
        transform: 'scale(0.98)',
      },

      // Delete icon styling
      '& .MuiChip-deleteIcon': {
        color: isOutlined ? mainColor : 'inherit',
        opacity: 0.7,
        transition: transitionPresets.button,
        '&:hover': { opacity: 1 },
      },

      '& .MuiChip-icon': {
        color: 'inherit',
      },
    };
  }

  // ── Apple Liquid Glass (original) ──
  return {
    position: 'relative',
    overflow: 'hidden',
    transition: transitionPresets.button,
    fontWeight: 500,
    borderRadius: 9999,
    fontSize: '0.8125rem',
    height: 32,

    '&.MuiChip-sizeSmall': {
      height: 24,
      fontSize: '0.75rem',
    },

    // ── Apple Liquid Glass: specular highlight (top-edge shine) ──
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: '50%',
      background: `linear-gradient(180deg, ${
        $isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.55)'
      } 0%, transparent 100%)`,
      borderRadius: 'inherit',
      pointerEvents: 'none' as const,
      zIndex: 1,
    },

    // ── Filled — Accent-tinted frosted glass ──
    ...(!isOutlined && {
      background: `linear-gradient(135deg, ${alpha(vibrant, 0.55)} 0%, ${alpha(vibrant, 0.72)} 100%)`,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `0.5px solid ${$isDark ? alpha(vibrant, 0.2) : 'rgba(255,255,255,0.35)'}`,
      boxShadow: `0 2px 8px ${alpha(vibrant, 0.22)}, 0 1px 3px rgba(0,0,0,0.06)`,
      color: '#fff',
    }),

    // ── Outlined — Frosted glass with accent border ──
    ...(isOutlined && {
      background: $isDark ? alpha(mainColor, 0.06) : alpha(mainColor, 0.05),
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderColor: alpha(mainColor, 0.3),
      boxShadow: $isDark ? '0 1px 4px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.05)',
      color: mainColor,
    }),

    '&:hover': {
      ...($glowOnHover && {
        boxShadow: getGlowValue($glowColor, 'medium'),
      }),
      ...($hoverScale && {
        transform: 'scale(1.05)',
      }),
      ...(!isOutlined && {
        background: `linear-gradient(135deg, ${alpha(vibrant, 0.65)} 0%, ${alpha(vibrant, 0.82)} 100%)`,
        boxShadow: `0 4px 16px ${alpha(vibrant, 0.3)}, 0 2px 6px rgba(0,0,0,0.08)`,
      }),
      ...(isOutlined && {
        background: $isDark ? alpha(mainColor, 0.12) : alpha(mainColor, 0.1),
        borderColor: alpha(mainColor, 0.5),
        boxShadow: $isDark
          ? `0 2px 8px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(mainColor, 0.08)}`
          : `0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px ${alpha(mainColor, 0.05)}`,
      }),
    },

    '&:active': {
      transform: 'scale(0.98)',
    },

    // Delete icon styling
    '& .MuiChip-deleteIcon': {
      color: isOutlined ? mainColor : 'inherit',
      opacity: 0.7,
      transition: transitionPresets.button,
      zIndex: 2,
      '&:hover': {
        opacity: 1,
        color: isOutlined ? mainColor : 'inherit',
      },
    },

    // Icon styling
    '& .MuiChip-icon': {
      color: 'inherit',
      zIndex: 2,
    },

    // Label above specular highlight
    '& .MuiChip-label': {
      zIndex: 2,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const GlassChip = forwardRef<HTMLDivElement, GlassChipProps>(
  (
    {
      glowOnHover = true,
      glowColor = 'primary',
      animate = false,
      animationDelay = 0,
      hoverScale = false,
      color = 'primary',
      variant = 'filled',
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const chip = (
      <StyledChip
        ref={ref}
        color={color}
        variant={variant}
        $isDark={isDark}
        $glowOnHover={glowOnHover}
        $glowColor={glowColor}
        $hoverScale={hoverScale}
        $chipColor={color}
        $chipVariant={variant}
        {...props}
      />
    );

    if (!animate) {
      return chip;
    }

    return (
      <motion.span
        initial={motionPresets.scaleIn.initial}
        animate={motionPresets.scaleIn.animate}
        transition={{
          ...motionPresets.scaleIn.transition,
          delay: animationDelay,
        }}
        style={{ display: 'inline-flex' }}
      >
        {chip}
      </motion.span>
    );
  },
);

GlassChip.displayName = 'GlassChip';

export default GlassChip;
